import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("fisio_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("fisio_token");
      localStorage.removeItem("fisio_user");
      localStorage.removeItem("fisio_tenant");
      if (window.location.pathname !== "/login" && !window.location.pathname.startsWith("/portal")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// Auth
export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  verifyCode: (data) => api.post("/auth/verify-code", data),
  resendCode: (data) => api.post("/auth/resend-code", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
};

// Patients
export const patientsAPI = {
  list: (params) => api.get("/patients", { params }),
  get: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post("/patients", data),
  update: (id, data) => api.patch(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
  statement: (id) => api.get(`/patients/${id}/statement`),
};

// Appointments
export const appointmentsAPI = {
  list: (params) => api.get("/appointments", { params }),
  create: (data) => api.post("/appointments", data),
  update: (id, data) => api.patch(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
};

// Episodes
export const episodesAPI = {
  list: (params) => api.get("/episodes", { params }),
  get: (id) => api.get(`/episodes/${id}`),
  create: (data) => api.post("/episodes", data),
  update: (id, data) => api.patch(`/episodes/${id}`, data),
  close: (id) => api.post(`/episodes/${id}/close`),
  // Fase 5c: consentimiento LFPDPPP por episodio.
  grantConsent: (id, data) => api.post(`/episodes/${id}/consent`, data || {}),
  revokeConsent: (id) => api.post(`/episodes/${id}/consent/revoke`),
};

// Evaluations
export const evaluationsAPI = {
  create: (episodeId, data) => api.post(`/episodes/${episodeId}/evaluations`, data),
  update: (id, data) => api.patch(`/evaluations/${id}`, data),
};

// Session Notes
export const sessionNotesAPI = {
  list: (episodeId) => api.get(`/episodes/${episodeId}/notes`),
  create: (episodeId, data) => api.post(`/episodes/${episodeId}/notes`, data),
  update: (id, data) => api.patch(`/notes/${id}`, data),
};

// Exercise Plans
export const exercisePlansAPI = {
  list: (params) => api.get("/exercise-plans", { params }),
  get: (id) => api.get(`/exercise-plans/${id}`),
  create: (data) => api.post("/exercise-plans", data),
  update: (id, data) => api.patch(`/exercise-plans/${id}`, data),
  delete: (id) => api.delete(`/exercise-plans/${id}`),
};

// Exercises (catálogo — Fase 1a)
export const exercisesAPI = {
  list: (params) => api.get("/exercises", { params }),
  get: (id) => api.get(`/exercises/${id}`),
  create: (data) => api.post("/exercises", data),
  update: (id, data) => api.patch(`/exercises/${id}`, data),
  delete: (id) => api.delete(`/exercises/${id}`),
  relations: (id) => api.get(`/exercises/${id}/relations`),
  addRelation: (id, data) => api.post(`/exercises/${id}/relations`, data),
};

// Program Cycles (Fase 3 — S&C)
export const programsAPI = {
  list: (params) => api.get("/program-cycles", { params }),
  get: (id) => api.get(`/program-cycles/${id}`),
  create: (data) => api.post("/program-cycles", data),
  update: (id, data) => api.patch(`/program-cycles/${id}`, data),
  delete: (id) => api.delete(`/program-cycles/${id}`),
};

// Payments
export const paymentsAPI = {
  list: (params) => api.get("/payments", { params }),
  create: (data) => api.post("/payments", data),
};

// Services
export const servicesAPI = {
  list: () => api.get("/services"),
  create: (data) => api.post("/services", data),
  update: (id, data) => api.patch(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Dashboard
export const dashboardAPI = {
  get: () => api.get("/dashboard"),
  charts: () => api.get("/dashboard/charts"),
};

// Exports
export const exportAPI = {
  payments: () => api.get("/export/payments", { responseType: "blob" }),
  appointments: () => api.get("/export/appointments", { responseType: "blob" }),
};

// Tenant
export const tenantAPI = {
  get: () => api.get("/tenant"),
  update: (data) => api.patch("/tenant", data),
};

// Team
export const teamAPI = {
  list: () => api.get("/team"),
  add: (data) => api.post("/team", data),
};

// Portal
export const portalAPI = {
  generateLink: (data) => api.post("/portal/generate-link", data),
  info: (token) => api.get("/portal/info", { params: { token } }),
  confirmAppointment: (data) => api.post("/portal/confirm-appointment", data),
  checkin: (data) => api.post("/portal/checkin", data),
};

// Relationships (staff — Fase 4): invitar al usuario final a su cuenta/portal
export const relationshipsAPI = {
  list: (params) => api.get("/relationships", { params }),
  invite: (data) => api.post("/relationships/invite", data),
  revoke: (id) => api.post(`/relationships/${id}/revoke`),
};

// ──────────────────────────────────────────────────────────────────────────
// EndUser (Fase 4b) — la cuenta real del paciente/atleta para la PWA /app.
// Usa su PROPIO axios + token (`enduser_token`), separado del `fisio_token` del staff.
// ──────────────────────────────────────────────────────────────────────────
export const ENDUSER_TOKEN_KEY = "enduser_token";
export const ENDUSER_DATA_KEY = "enduser_data";

const euApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

euApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(ENDUSER_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

euApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(ENDUSER_TOKEN_KEY);
      localStorage.removeItem(ENDUSER_DATA_KEY);
      const p = window.location.pathname;
      // No redirigir desde las pantallas públicas de la PWA (login/claim).
      if (p.startsWith("/app") && p !== "/app/login" && p !== "/app/claim") {
        window.location.href = "/app/login";
      }
    }
    return Promise.reject(err);
  }
);

export const endUserAPI = {
  login: (data) => euApi.post("/enduser/login", data),
  claim: (data) => euApi.post("/enduser/claim", data),
  me: () => euApi.get("/enduser/me"),
  plans: () => euApi.get("/enduser/plans"),
  sessions: () => euApi.get("/enduser/sessions"),
  createSession: (data) => euApi.post("/enduser/sessions", data),
  // Fase 5e: derecho ARCO de cancelación — borra datos S&C y la cuenta
  // (las sesiones clínicas se retienen por NOM-024).
  arcoCancel: () => euApi.post("/enduser/arco/cancel"),
};

// Notifications (MOCK)
export const notificationsAPI = {
  sendReminder: (data) => api.post("/notifications/send-reminder", data),
  sendPortalLink: (data) => api.post("/notifications/send-portal-link", data),
};
